import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Building2, Smartphone, Phone, Loader2, ShieldCheck, Copy, ArrowLeft, CheckCircle2, RefreshCw, Upload, FileCheck2, X, Download, AlertTriangle, Sparkles, Check } from 'lucide-react';
import { toast } from 'sonner';

type Method = 'bank_transfer' | 'telebirr' | 'santimpay' | 'talk_to_sales';

const METHODS: { value: Method; label: string; desc: string; icon: any }[] = [
  { value: 'bank_transfer', label: 'Bank Transfer', desc: 'CBE / Awash / Dashen — verified within 1 business day', icon: Building2 },
  { value: 'telebirr',      label: 'Telebirr',      desc: 'Send from your Telebirr wallet to our merchant',     icon: Smartphone },
  { value: 'santimpay',     label: 'SantimPay',     desc: 'Pay through SantimPay and share your reference',     icon: Smartphone },
  { value: 'talk_to_sales', label: 'Talk to Sales', desc: 'Custom invoice / corporate billing',                 icon: Phone },
];

const BANK_ACCOUNTS = [
  { bank: 'Commercial Bank of Ethiopia (CBE)', account: '1000-XXXX-XXXX', name: 'EthioRealEstates PLC' },
  { bank: 'Awash Bank',                        account: '0130-XXXX-XXXX', name: 'EthioRealEstates PLC' },
  { bank: 'Dashen Bank',                       account: '5544-XXXX-XXXX', name: 'EthioRealEstates PLC' },
];

const TELEBIRR_NUMBER = '+251 911 000 000';
const SANTIMPAY_MERCHANT = 'EthioRealEstates';

function generateReference(method: Method) {
  const prefix = method === 'telebirr' ? 'TB' : method === 'bank_transfer' ? 'BT' : method === 'santimpay' ? 'SP' : 'WTU';
  const ts = Date.now().toString(36).toUpperCase();
  const rand = Math.random().toString(36).slice(2, 6).toUpperCase();
  return `${prefix}-${ts}-${rand}`;
}

const methodLabel = (m: Method) => METHODS.find(x => x.value === m)?.label ?? m;

const ALLOWED_MIME = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'application/pdf'];
const MAX_BYTES = 10 * 1024 * 1024;
const MAX_RECEIPTS = 5;

interface UploadedReceipt {
  id: string;
  file: File;
  path: string;
  url: string | null;
  ocr?: { reference?: string | null; amount?: number | null; bank?: string | null } | null;
}

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  defaultAmount?: number;
  defaultMethod?: Method;
  defaultReference?: string;
  defaultContactPhone?: string;
}

export function TopUpDialog({ open, onOpenChange, defaultAmount, defaultMethod, defaultReference, defaultContactPhone }: Props) {
  const qc = useQueryClient();
  const [step, setStep] = useState<'form' | 'review'>('form');
  const [amount, setAmount] = useState<string>(String(defaultAmount ?? 5000));
  const [method, setMethod] = useState<Method>(defaultMethod ?? 'bank_transfer');
  const [bankName, setBankName] = useState('');
  const [reference, setReference] = useState(defaultReference ?? '');
  const [contactPhone, setContactPhone] = useState(defaultContactPhone ?? '');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [receipts, setReceipts] = useState<UploadedReceipt[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [uploadingCount, setUploadingCount] = useState(0);
  const [dupes, setDupes] = useState<Array<{ id: string; user_id: string; amount_etb: number; payment_reference: string; status: string; created_at: string; same_user: boolean; amount_matches: boolean; bank_name?: string | null }>>([]);
  const [checkingDupes, setCheckingDupes] = useState(false);

  // Fetch enabled payment methods from DB
  const { data: enabledMethods } = useQuery({
    queryKey: ['payment-methods-enabled'],
    enabled: open,
    queryFn: async () => {
      const { data } = await supabase
        .from('payment_methods' as any)
        .select('code, label, description, sort_order, is_enabled')
        .eq('is_enabled', true)
        .order('sort_order', { ascending: true });
      return (data as any[]) || [];
    },
  });

  const availableMethods = useMemo(() => {
    if (!enabledMethods) return METHODS;
    const enabledSet = new Set(enabledMethods.map((m) => m.code));
    return METHODS
      .filter((m) => enabledSet.has(m.value))
      .map((m) => {
        const dbm = enabledMethods.find((x) => x.code === m.value);
        return dbm ? { ...m, label: dbm.label || m.label, desc: dbm.description || m.desc } : m;
      });
  }, [enabledMethods]);

  // If currently selected method becomes disabled, fall back to first available
  useEffect(() => {
    if (!enabledMethods || availableMethods.length === 0) return;
    if (!availableMethods.find((m) => m.value === method)) {
      setMethod(availableMethods[0].value);
    }
  }, [enabledMethods, availableMethods, method]);


  useEffect(() => {
    if (open) {
      setStep('form');
      setAmount(String(defaultAmount ?? 5000));
      const m = defaultMethod ?? 'bank_transfer';
      setMethod(m);
      setBankName('');
      setReference(defaultReference ?? generateReference(m));
      setContactPhone(defaultContactPhone ?? '');
      setNotes('');
      setReceipts([]);
    }
  }, [open, defaultAmount, defaultMethod, defaultReference, defaultContactPhone]);

  // When user changes method inside the dialog, regenerate reference if it was auto-generated
  useEffect(() => {
    if (!open) return;
    setReference((prev) => {
      // Regenerate if empty or if it doesn't match new method's prefix pattern
      const expectedPrefix = method === 'telebirr' ? 'TB-' : method === 'bank_transfer' ? 'BT-' : method === 'santimpay' ? 'SP-' : 'WTU-';
      if (!prev || /^(TB|BT|SP|WTU)-[A-Z0-9]+-[A-Z0-9]+$/.test(prev)) {
        return method === 'talk_to_sales' ? '' : generateReference(method);
      }
      return prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [method]);

  // Debounced duplicate-reference detection
  useEffect(() => {
    if (!open) return;
    const ref = reference.trim();
    // Skip auto-generated placeholder refs
    if (!ref || ref.length < 4 || /^(TB|BT|SP|WTU)-[A-Z0-9]+-[A-Z0-9]+$/.test(ref)) {
      setDupes([]);
      return;
    }
    const amt = Number(amount) || null;
    setCheckingDupes(true);
    const t = setTimeout(async () => {
      const { data, error } = await supabase.rpc('check_topup_duplicate' as any, {
        _reference: ref,
        _amount: amt,
        _exclude_request: null,
      });
      if (!error && Array.isArray(data)) setDupes(data as any);
      setCheckingDupes(false);
    }, 450);
    return () => { clearTimeout(t); setCheckingDupes(false); };
  }, [open, reference, amount]);

  const applyOcrToForm = (ocr: { reference?: string | null; amount?: number | null; bank?: string | null } | null | undefined) => {
    if (!ocr) return;
    if (ocr.reference) setReference(ocr.reference);
    if (ocr.amount && ocr.amount > 0) setAmount(String(ocr.amount));
    if (ocr.bank) setBankName(ocr.bank);
    toast.success('Applied to top-up details');
  };

  const updateReceiptOcr = (id: string, patch: Partial<NonNullable<UploadedReceipt['ocr']>>) => {
    setReceipts((rs) => rs.map((r) => r.id === id
      ? { ...r, ocr: { reference: r.ocr?.reference ?? null, amount: r.ocr?.amount ?? null, bank: r.ocr?.bank ?? null, ...patch } }
      : r));
  };

  const validateFile = (file: File): string | null => {
    if (!ALLOWED_MIME.includes(file.type)) return 'Allowed: JPG, PNG, WEBP, HEIC or PDF';
    if (file.size > MAX_BYTES) return 'File must be 10MB or smaller';
    if (file.size === 0) return 'File appears empty';
    return null;
  };

  const runOcr = async (storagePath: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('ocr-bank-receipt', {
        body: { storagePath },
      });
      if (error || !data?.ok || data?.skipped) return null;
      const ex = data.extracted || {};
      const ocr = {
        reference: typeof ex.reference === 'string' ? ex.reference.trim() : null,
        amount: typeof ex.amount === 'number' ? ex.amount
              : typeof ex.amount === 'string' ? Number(String(ex.amount).replace(/[^\d.]/g, '')) || null
              : null,
        bank: typeof ex.bank === 'string' ? ex.bank : null,
      };
      // Prefill only when fields are empty/auto-generated
      if (ocr.reference) {
        setReference((prev) => (!prev || /^(TB|BT|SP|WTU)-[A-Z0-9]+-[A-Z0-9]+$/.test(prev) ? ocr.reference! : prev));
      }
      if (ocr.amount && ocr.amount > 0) {
        setAmount((prev) => (!prev || prev === '5000' || prev === '0' ? String(ocr.amount) : prev));
      }
      if (ocr.bank) setBankName((prev) => prev || ocr.bank!);
      if (ocr.reference || ocr.amount) toast.success('Receipt scanned — fields prefilled');
      return ocr;
    } catch {
      return null;
    }
  };

  const handleFiles = async (files: File[]) => {
    if (!files.length) return;
    if (receipts.length + files.length > MAX_RECEIPTS) {
      return toast.error(`You can upload at most ${MAX_RECEIPTS} receipts`);
    }
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return toast.error('Not signed in');

    for (const file of files) {
      const err = validateFile(file);
      if (err) { toast.error(`${file.name}: ${err}`); continue; }
      setUploadingCount((n) => n + 1);
      try {
        const ext = file.name.split('.').pop()?.toLowerCase() || 'bin';
        const path = `${user.id}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
        const { error: upErr } = await supabase.storage
          .from('wallet-receipts')
          .upload(path, file, { contentType: file.type, upsert: false });
        if (upErr) throw upErr;
        const { data: signed } = await supabase.storage
          .from('wallet-receipts')
          .createSignedUrl(path, 60 * 60 * 24 * 7);
        const id = `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
        setReceipts((r) => [...r, { id, file, path, url: signed?.signedUrl ?? null, ocr: null }]);
        // Best-effort OCR (images only)
        if (file.type.startsWith('image/')) {
          const ocr = await runOcr(path);
          if (ocr) setReceipts((r) => r.map((x) => (x.path === path ? { ...x, ocr } : x)));
        }
      } catch (e: any) {
        toast.error(e.message || `Upload failed for ${file.name}`);
      } finally {
        setUploadingCount((n) => Math.max(0, n - 1));
      }
    }
  };

  const removeReceipt = async (id: string) => {
    const r = receipts.find((x) => x.id === id);
    if (!r) return;
    await supabase.storage.from('wallet-receipts').remove([r.path]);
    setReceipts((rs) => rs.filter((x) => x.id !== id));
  };

  const downloadReceipt = async (r: UploadedReceipt) => {
    try {
      const { data, error } = await supabase.storage
        .from('wallet-receipts').download(r.path);
      if (error || !data) throw error || new Error('Download failed');
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url; a.download = r.file.name; a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      toast.error(e.message || 'Download failed');
    }
  };

  const goReview = () => {
    const amt = Number(amount);
    if (!amt || amt < 100) return toast.error('Minimum top-up is 100 ETB');
    if ((method === 'bank_transfer' || method === 'telebirr' || method === 'santimpay') && !reference.trim()) {
      return toast.error('Please enter your payment reference / transaction ID');
    }
    if (method === 'bank_transfer' && receipts.length === 0) {
      return toast.error('Please upload at least one bank transfer receipt');
    }
    if (uploadingCount > 0) {
      return toast.error('Please wait for receipts to finish uploading');
    }
    if (method === 'talk_to_sales' && !contactPhone.trim()) {
      return toast.error('Please enter a phone number our sales team can call');
    }
    setStep('review');
  };

  const submit = async () => {
    const amt = Number(amount);
    setSubmitting(true);
    const { data: requestId, error } = await supabase.rpc('request_wallet_topup' as any, {
      _amount: amt,
      _method: method,
      _bank_name: method === 'bank_transfer' ? (bankName.trim() || null) : null,
      _payment_reference: reference.trim() || null,
      _proof_url: receipts[0]?.path ?? null,
      _contact_phone: contactPhone.trim() || null,
      _notes: notes.trim() || null,
    });
    if (error) { setSubmitting(false); return toast.error(error.message); }

    // Attach all receipts (if any) to the new request
    for (const r of receipts) {
      await supabase.rpc('attach_topup_receipt' as any, {
        _request_id: requestId,
        _storage_path: r.path,
        _file_name: r.file.name,
        _mime_type: r.file.type,
        _size_bytes: r.file.size,
        _ocr: r.ocr ?? null,
      });
    }

    setSubmitting(false);

    toast.success(method === 'talk_to_sales'
      ? 'Request submitted — our sales team will reach out shortly.'
      : 'Top-up request submitted. We will credit your wallet once payment is verified.');
    qc.invalidateQueries({ queryKey: ['wallet-topup-requests'] });
    qc.invalidateQueries({ queryKey: ['wallet-topup-pending'] });
    qc.invalidateQueries({ queryKey: ['wallet'] });
    onOpenChange(false);
  };

  const copy = (s: string) => { navigator.clipboard.writeText(s); toast.success('Copied'); };
  const regenRef = () => { setReference(generateReference(method)); toast.success('New reference generated'); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{step === 'review' ? 'Review your top-up' : 'Top up Wallet'}</DialogTitle>
          <DialogDescription>
            {step === 'review'
              ? 'Please confirm the details below before we create your top-up request.'
              : 'Add ETB balance to spend on packages and credit packs.'}
          </DialogDescription>
        </DialogHeader>

        {step === 'form' && (
          <div className="space-y-4">
            <div>
              <Label>Amount (ETB) *</Label>
              <Input type="number" min={100} value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="5000" />
              <div className="flex gap-2 mt-2 flex-wrap">
                {[1000, 5000, 10000, 25000, 50000].map((v) => (
                  <Button key={v} type="button" size="sm" variant="outline" className="h-7 text-xs"
                    onClick={() => setAmount(String(v))}>
                    {v.toLocaleString()}
                  </Button>
                ))}
              </div>
            </div>

            <div>
              <Label className="mb-2 block">Payment method *</Label>
              <RadioGroup value={method} onValueChange={(v) => setMethod(v as Method)} className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {availableMethods.map((m) => {
                  const Icon = m.icon;
                  const selected = method === m.value;
                  return (
                    <label key={m.value} htmlFor={`tu-${m.value}`}
                      className={`flex items-start gap-2 rounded-lg border p-3 cursor-pointer ${selected ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'}`}>
                      <RadioGroupItem value={m.value} id={`tu-${m.value}`} className="mt-0.5" />
                      <Icon className="h-4 w-4 mt-0.5 text-primary shrink-0" />
                      <div>
                        <p className="text-sm font-medium leading-none">{m.label}</p>
                        <p className="text-[11px] text-muted-foreground mt-1">{m.desc}</p>
                      </div>
                    </label>
                  );
                })}
              </RadioGroup>
            </div>

            {method === 'bank_transfer' && (
              <div className="rounded-lg border bg-muted/30 p-3 space-y-2">
                <p className="text-xs font-medium">Transfer to one of our accounts:</p>
                {BANK_ACCOUNTS.map((b) => (
                  <div key={b.bank} className="flex items-center justify-between bg-background rounded p-2 text-xs">
                    <div>
                      <p className="font-medium">{b.bank}</p>
                      <p className="text-muted-foreground">{b.name} · <span className="font-mono">{b.account}</span></p>
                    </div>
                    <Button type="button" size="sm" variant="ghost" className="h-7" onClick={() => copy(b.account)}>
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Bank used</Label>
                    <Input value={bankName} onChange={(e) => setBankName(e.target.value)} placeholder="e.g. CBE" />
                  </div>
                  <div>
                    <Label className="text-xs">Transfer reference *</Label>
                    <div className="flex gap-1">
                      <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="FT…" />
                      <Button type="button" size="icon" variant="ghost" className="h-9 w-9 shrink-0" onClick={regenRef} title="Regenerate">
                        <RefreshCw className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="space-y-2 pt-1">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs">Bank receipts / payment slips *</Label>
                    <span className="text-[10px] text-muted-foreground">
                      {receipts.length}/{MAX_RECEIPTS}
                    </span>
                  </div>

                  <label
                    htmlFor="bank-receipts"
                    onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
                    onDragEnter={(e) => { e.preventDefault(); setDragActive(true); }}
                    onDragLeave={(e) => { e.preventDefault(); setDragActive(false); }}
                    onDrop={(e) => {
                      e.preventDefault();
                      setDragActive(false);
                      const files = Array.from(e.dataTransfer.files || []);
                      if (files.length) handleFiles(files);
                    }}
                    className={`flex flex-col items-center justify-center gap-1 rounded-md border-2 border-dashed bg-background px-3 py-5 text-xs cursor-pointer transition ${
                      dragActive ? 'border-primary bg-primary/5' : 'hover:bg-muted/40'
                    } ${receipts.length >= MAX_RECEIPTS ? 'opacity-50 pointer-events-none' : ''}`}
                  >
                    {uploadingCount > 0 ? (
                      <Loader2 className="h-5 w-5 animate-spin text-primary" />
                    ) : (
                      <Upload className="h-5 w-5 text-primary" />
                    )}
                    <span className="font-medium">
                      {uploadingCount > 0
                        ? `Uploading ${uploadingCount} file${uploadingCount > 1 ? 's' : ''}…`
                        : dragActive ? 'Drop to upload' : 'Drag & drop or click to upload'}
                    </span>
                    <span className="text-[11px] text-muted-foreground">
                      JPG, PNG, WEBP, HEIC or PDF · up to 10MB each · max {MAX_RECEIPTS}
                    </span>
                    <input
                      id="bank-receipts"
                      type="file"
                      multiple
                      accept="image/jpeg,image/png,image/webp,image/heic,application/pdf"
                      className="hidden"
                      disabled={uploadingCount > 0 || receipts.length >= MAX_RECEIPTS}
                      onChange={(e) => {
                        const files = Array.from(e.target.files || []);
                        if (files.length) handleFiles(files);
                        e.currentTarget.value = '';
                      }}
                    />
                  </label>

                  {receipts.length > 0 && (
                    <ul className="space-y-2">
                      {receipts.map((r) => (
                        <li key={r.id} className="rounded-md border bg-background p-2 text-xs space-y-2">
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                              <FileCheck2 className="h-4 w-4 text-emerald-600 shrink-0" />
                              <div className="min-w-0">
                                <p className="truncate font-medium">{r.file.name}</p>
                                <p className="text-muted-foreground">{(r.file.size / 1024).toFixed(0)} KB</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              {r.url && (
                                <a href={r.url} target="_blank" rel="noopener noreferrer"
                                  className="text-primary hover:underline px-1">View</a>
                              )}
                              <Button type="button" size="icon" variant="ghost" className="h-6 w-6"
                                onClick={() => downloadReceipt(r)} title="Download">
                                <Download className="h-3 w-3" />
                              </Button>
                              <Button type="button" size="icon" variant="ghost" className="h-6 w-6"
                                onClick={() => removeReceipt(r.id)} title="Remove">
                                <X className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>

                          {r.ocr && (r.ocr.reference || r.ocr.amount || r.ocr.bank) && (
                            <div className="rounded-md border border-dashed bg-primary/5 p-2 space-y-1.5">
                              <div className="flex items-center justify-between gap-2">
                                <span className="flex items-center gap-1 font-medium text-[11px]">
                                  <Sparkles className="h-3 w-3 text-primary" /> Extracted from receipt — review &amp; edit
                                </span>
                                <Button type="button" size="sm" variant="ghost" className="h-6 text-[11px] px-2"
                                  onClick={() => applyOcrToForm(r.ocr)} title="Apply these values to the form above">
                                  <Check className="h-3 w-3 mr-1" /> Apply
                                </Button>
                              </div>
                              <div className="grid grid-cols-3 gap-1.5">
                                <div>
                                  <Label className="text-[10px] text-muted-foreground">Reference</Label>
                                  <Input value={r.ocr.reference ?? ''}
                                    onChange={(e) => updateReceiptOcr(r.id, { reference: e.target.value })}
                                    className="h-7 text-[11px] font-mono" placeholder="—" />
                                </div>
                                <div>
                                  <Label className="text-[10px] text-muted-foreground">Amount (ETB)</Label>
                                  <Input type="number" value={r.ocr.amount ?? ''}
                                    onChange={(e) => updateReceiptOcr(r.id, { amount: e.target.value === '' ? null : Number(e.target.value) })}
                                    className="h-7 text-[11px]" placeholder="—" />
                                </div>
                                <div>
                                  <Label className="text-[10px] text-muted-foreground">Bank</Label>
                                  <Input value={r.ocr.bank ?? ''}
                                    onChange={(e) => updateReceiptOcr(r.id, { bank: e.target.value })}
                                    className="h-7 text-[11px]" placeholder="—" />
                                </div>
                              </div>
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}

                  {dupes.length > 0 && (
                    <Alert variant="destructive" className="py-2">
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription className="text-[11px]">
                        <span className="font-semibold">Duplicate reference detected.</span> This reference is already on{' '}
                        {dupes.length} other top-up request{dupes.length > 1 ? 's' : ''}
                        {dupes.some((d) => d.amount_matches) ? ' with a matching amount' : ''}
                        {dupes.some((d) => !d.same_user) ? ' (including from another account)' : ' (your previous request)'}.
                        Please double-check before submitting.
                        <ul className="mt-1 space-y-0.5">
                          {dupes.slice(0, 3).map((d) => (
                            <li key={d.id} className="font-mono">
                              · {Number(d.amount_etb).toLocaleString()} ETB · {d.status} · {new Date(d.created_at).toLocaleDateString()}
                              {d.same_user ? ' · yours' : ' · other user'}
                            </li>
                          ))}
                        </ul>
                      </AlertDescription>
                    </Alert>
                  )}
                  <p className="text-[11px] text-muted-foreground">
                    Receipts are scanned automatically — reference & amount may prefill. Visible only to you and Accounting.
                  </p>
                </div>
              </div>
            )}

            {method === 'telebirr' && (
              <div className="rounded-lg border bg-muted/30 p-3 space-y-2 text-xs">
                <p className="font-medium">Send to Telebirr merchant:</p>
                <div className="flex items-center justify-between bg-background rounded p-2">
                  <span className="font-mono">{TELEBIRR_NUMBER}</span>
                  <Button type="button" size="sm" variant="ghost" className="h-7" onClick={() => copy(TELEBIRR_NUMBER)}>
                    <Copy className="h-3 w-3" />
                  </Button>
                </div>
                <Label className="text-xs">Telebirr transaction ID *</Label>
                <div className="flex gap-1">
                  <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="CGD…" />
                  <Button type="button" size="icon" variant="ghost" className="h-9 w-9 shrink-0" onClick={regenRef} title="Regenerate">
                    <RefreshCw className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            )}

            {method === 'santimpay' && (
              <div className="rounded-lg border bg-muted/30 p-3 space-y-2 text-xs">
                <p className="font-medium">Pay via SantimPay merchant:</p>
                <div className="flex items-center justify-between bg-background rounded p-2">
                  <span className="font-mono">{SANTIMPAY_MERCHANT}</span>
                  <Button type="button" size="sm" variant="ghost" className="h-7" onClick={() => copy(SANTIMPAY_MERCHANT)}>
                    <Copy className="h-3 w-3" />
                  </Button>
                </div>
                <Label className="text-xs">SantimPay reference *</Label>
                <div className="flex gap-1">
                  <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="SP…" />
                  <Button type="button" size="icon" variant="ghost" className="h-9 w-9 shrink-0" onClick={regenRef} title="Regenerate">
                    <RefreshCw className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            )}

            {method !== 'talk_to_sales' && (
              <div>
                <Label>Contact phone (optional)</Label>
                <Input value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} placeholder="+251…" />
              </div>
            )}

            {method === 'talk_to_sales' && (
              <div>
                <Label>Best phone to reach you *</Label>
                <Input value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} placeholder="+251…" />
              </div>
            )}

            <div>
              <Label>Notes (optional)</Label>
              <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                placeholder="Anything our team should know…" />
            </div>

            <Alert>
              <ShieldCheck className="h-4 w-4" />
              <AlertDescription className="text-xs">
                Your wallet is credited automatically once our team verifies the payment (usually within 1 business day).
              </AlertDescription>
            </Alert>
          </div>
        )}

        {step === 'review' && (
          <div className="space-y-3">
            <div className="rounded-lg border bg-muted/30 divide-y">
              <Row label="Amount" value={`${Number(amount).toLocaleString()} ETB`} highlight />
              <Row label="Payment method" value={methodLabel(method)} />
              {method === 'bank_transfer' && bankName && <Row label="Bank used" value={bankName} />}
              {method !== 'talk_to_sales' && reference && (
                <Row label="Payment reference" value={reference} mono />
              )}
              {contactPhone && <Row label="Contact phone" value={contactPhone} />}
              {receipts.length > 0 && (
                <div className="p-3 text-sm">
                  <p className="text-muted-foreground mb-2">
                    Bank receipts ({receipts.length})
                  </p>
                  <ul className="space-y-1.5">
                    {receipts.map((r) => (
                      <li key={r.id} className="flex items-center justify-between gap-2 text-xs">
                        <div className="flex items-center gap-2 min-w-0">
                          <FileCheck2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                          <span className="truncate font-medium">{r.file.name}</span>
                          <span className="text-muted-foreground shrink-0">
                            {(r.file.size / 1024).toFixed(0)} KB
                          </span>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          {r.url && (
                            <a href={r.url} target="_blank" rel="noopener noreferrer"
                              className="text-primary hover:underline">View</a>
                          )}
                          <Button type="button" size="icon" variant="ghost" className="h-6 w-6"
                            onClick={() => downloadReceipt(r)} title="Download">
                            <Download className="h-3 w-3" />
                          </Button>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {notes && <Row label="Notes" value={notes} />}
            </div>

            {dupes.length > 0 && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription className="text-xs">
                  <span className="font-semibold">Duplicate reference detected.</span> The reference{' '}
                  <span className="font-mono">{reference}</span> is already on {dupes.length} other top-up request
                  {dupes.length > 1 ? 's' : ''}
                  {dupes.some((d) => d.amount_matches) ? ' with a matching amount' : ''}. Please confirm this is not a re-submission before continuing.
                </AlertDescription>
              </Alert>
            )}

            <Alert>
              <CheckCircle2 className="h-4 w-4" />
              <AlertDescription className="text-xs">
                {method === 'talk_to_sales'
                  ? 'Our sales team will reach out using the phone number above to finalise this top-up.'
                  : `Please make sure you have already sent ${Number(amount).toLocaleString()} ETB via ${methodLabel(method)} using the reference above. We will credit your wallet once verified.`}
              </AlertDescription>
            </Alert>
          </div>
        )}

        <DialogFooter>
          {step === 'form' ? (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
              <Button onClick={goReview} disabled={submitting}>Review</Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => setStep('form')} disabled={submitting}>
                <ArrowLeft className="h-4 w-4 mr-1" /> Back
              </Button>
              <Button onClick={submit} disabled={submitting}>
                {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Confirm & Submit
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, value, highlight, mono }: { label: string; value: string; highlight?: boolean; mono?: boolean }) {
  return (
    <div className="flex items-start justify-between gap-3 p-3 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className={`text-right ${highlight ? 'font-semibold text-base' : 'font-medium'} ${mono ? 'font-mono text-xs' : ''}`}>
        {value}
      </span>
    </div>
  );
}
